import React, { useState } from 'react'
import clsx from 'clsx'
import type { UsageBreakdownRow } from '@shared/common-types.js'
import { UNKNOWN_LABEL } from './format.js'

type Props = {
  title: string
  icon?: React.ReactNode
  /** 右上角的列名，「会话」或「次数」 */
  countLabel: string
  rows: UsageBreakdownRow[]
  /** 算百分比的分母；不给就不显示百分比 */
  total?: number
  /** 当前选中的过滤值 */
  selected: string | undefined
  /** 点一行：选中它当过滤，再点一次取消 */
  onSelect: (value: string | undefined) => void
  renderLabel?: (row: UsageBreakdownRow) => React.ReactNode
}

const numberFormat = new Intl.NumberFormat('zh-CN')

/** 手机上默认显示几行。手机上不套内层滚动（手指滑动会卡在列表里），多出来的点「显示全部」 */
const MOBILE_COLLAPSED_ROWS = 8

function formatShare(share: number): string {
  return share > 0 && share < 0.005 ? '<1%' : `${Math.round(share * 100)}%`
}

/** Aptabase 那种列表：每行底下一条按数量比例画的浅色条，点一行就按它过滤 */
export default function BreakdownCard({
  title,
  icon,
  countLabel,
  rows,
  total,
  selected,
  onSelect,
  renderLabel,
}: Props) {
  const [expanded, setExpanded] = useState(false)
  const max = Math.max(1, ...rows.map((row) => row.count))
  return (
    <div className="flex flex-col rounded-xl bg-white p-3 dark:bg-slate-700 md:p-4">
      <div className="mb-2 flex items-center justify-between md:mb-3">
        <div className="flex items-center gap-2 text-base font-semibold dark:text-white">
          {icon}
          {title}
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">{countLabel}</span>
      </div>
      {rows.length === 0 ? (
        <div className="flex h-24 items-center justify-center text-sm text-gray-400">没有数据</div>
      ) : (
        <ul className="flex flex-col gap-1 md:max-h-[440px] md:overflow-y-auto md:pr-1">
          {rows.map((row, index) => {
            const isSelected = selected === row.value
            return (
              <li key={row.value} className={clsx(!expanded && index >= MOBILE_COLLAPSED_ROWS && 'hidden md:block')}>
                <button
                  type="button"
                  title={`${numberFormat.format(row.count)} ${countLabel} · ${numberFormat.format(row.people)} 人`}
                  onClick={() => onSelect(isSelected ? undefined : row.value)}
                  className={clsx(
                    'relative flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm',
                    'hover:bg-gray-50 dark:hover:bg-slate-600',
                    isSelected && 'ring-2 ring-inset ring-blue-500',
                  )}
                >
                  <span
                    className="absolute inset-y-0 left-0 rounded-md bg-blue-100 dark:bg-blue-500/25"
                    style={{ width: `${(row.count / max) * 100}%` }}
                  />
                  <span className="relative flex min-w-0 items-center gap-2 truncate">
                    {renderLabel ? renderLabel(row) : row.label || UNKNOWN_LABEL}
                  </span>
                  <span className="relative flex shrink-0 items-baseline gap-2 tabular-nums">
                    <span className="text-xs text-gray-500 dark:text-gray-400">{numberFormat.format(row.count)}</span>
                    {total !== undefined && total > 0 && (
                      <span className="w-10 text-right">{formatShare(row.count / total)}</span>
                    )}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
      {rows.length > MOBILE_COLLAPSED_ROWS && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="mt-2 rounded-md py-1.5 text-sm text-blue-500 md:hidden"
        >
          {expanded ? '收起' : `显示全部 ${rows.length} 行`}
        </button>
      )}
    </div>
  )
}
